// PRO+ hub photo wall: edge to edge, no gaps, every photo whole. Editors see hidden photos (dimmed) and pick what shows.
import { useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Eye, EyeOff, Star, Trash2, ImagePlus } from "lucide-react";
import { API } from "../../App";
import { abs, errMsg } from "../voltbox/vbShared";
import { PhotoLightbox } from "../voltbox/PhotoGrid";
import { HubHead } from "./HubSections";
import { hubAuth, useHubPhotos } from "./hubApi";

const ToolBtn = ({ onClick, title, children, testid, on }) => (
  <button type="button" onClick={onClick} title={title} aria-label={title} className={`h-8 w-8 rounded-full flex items-center justify-center backdrop-blur-md transition-colors ${on ? "bg-[#54E3FF] text-[#030608]" : "bg-black/70 text-white hover:bg-black/90"}`} data-testid={testid}>{children}</button>
);

const EditorTools = ({ slug, p, onChanged }) => {
  const act = async (fn, ok) => { try { await fn(); toast.success(ok); onChanged(); } catch (e) { toast.error(errMsg(e, "Couldn't do that")); } };
  const base = `${API}/admin/bands/${slug}`;
  return (
    <div className="absolute top-2 right-2 flex gap-1.5 z-10">
      <ToolBtn on={!p.hidden} title={p.hidden ? "Show on the page" : "Hide from the page"} testid={`hub-photo-toggle-${p.photo_id}`} onClick={() => act(() => axios.patch(`${base}/photos/${p.photo_id}`, { hidden: !p.hidden }, hubAuth()), p.hidden ? "Showing on the page" : "Hidden")}>{p.hidden ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</ToolBtn>
      <ToolBtn title="Use as the lead photo" testid={`hub-photo-hero-${p.photo_id}`} onClick={() => act(() => axios.put(`${base}/hero`, { photo_id: p.photo_id }, hubAuth()), "Lead photo set")}><Star className="h-4 w-4" /></ToolBtn>
      <ToolBtn title="Remove" testid={`hub-photo-remove-${p.photo_id}`} onClick={() => window.confirm("Remove this photo for good?") && act(() => axios.delete(`${base}/photos/${p.photo_id}`, hubAuth()), "Removed")}><Trash2 className="h-4 w-4" /></ToolBtn>
    </div>
  );
};

const Tile = ({ p, editor, slug, onOpen, onChanged }) => (
  <figure className="relative m-0 break-inside-avoid group overflow-hidden" data-testid={`hub-photo-${p.photo_id}`} data-hidden={p.hidden ? "true" : "false"}>
    <button type="button" onClick={p.hidden ? undefined : onOpen} className={`block w-full p-0 border-0 bg-transparent ${p.hidden ? "cursor-default" : "cursor-zoom-in"}`} aria-label={p.caption || "Open photo"}>
      <img src={abs(p.url)} alt={p.caption || ""} loading="lazy" width={p.w || undefined} height={p.h || undefined} style={p.w && p.h ? { aspectRatio: `${p.w} / ${p.h}` } : undefined} className={`block w-full h-auto transition-[opacity,filter,transform] duration-500 ${p.hidden ? "opacity-30 grayscale" : "group-hover:scale-[1.02] group-hover:brightness-110"}`} />
    </button>
    {!p.hidden && <span className="pointer-events-none absolute inset-0 shadow-[inset_0_0_0_0_rgba(84,227,255,0)] group-hover:shadow-[inset_0_0_0_2px_rgba(84,227,255,0.8)] transition-shadow" />}
    {editor && p.hidden && <span className="absolute top-2 left-2 rounded-full bg-black/80 px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.2em] text-white/80">Hidden</span>}
    {editor && <EditorTools slug={slug} p={p} onChanged={onChanged} />}
  </figure>
);

const AddPhotos = ({ slug, onDone }) => {
  const input = useRef(null);
  const [busy, setBusy] = useState(false);
  const upload = async (files) => {
    setBusy(true);
    for (const f of [...(files || [])]) {
      const fd = new FormData(); fd.append("file", f);
      try { await axios.post(`${API}/admin/bands/${slug}/photos`, fd, hubAuth()); } catch (e) { toast.error(`${f.name}: ${errMsg(e, "couldn't upload")}`); }
    }
    setBusy(false); onDone();
  };
  return (
    <>
      <button type="button" onClick={() => input.current?.click()} disabled={busy} className="inline-flex items-center gap-2 rounded-full bg-[#54E3FF] text-[#030608] px-4 py-2 text-xs font-black disabled:opacity-50" data-testid="hub-photo-add"><ImagePlus className="h-4 w-4" /> {busy ? "Uploading…" : "Add photos"}</button>
      <input ref={input} type="file" multiple accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => { upload(e.target.files); e.target.value = ""; }} data-testid="hub-photo-input" />
    </>
  );
};

export const HubPhotoWall = ({ slug, photos = [], editor, onChanged }) => {
  const [list, reloadAll] = useHubPhotos(slug, editor, photos);
  const [zoom, setZoom] = useState(null);
  const visible = list.filter((p) => !p.hidden);
  const n = list.length;
  const changed = () => { reloadAll(); onChanged(); };
  if (!list.length && !editor) return null;
  return (
    <section id="photos" className="scroll-mt-24 bg-black border-t border-[#54E3FF]/20" data-testid="hub-photo-wall">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-14 pb-2 flex items-end justify-between gap-4 flex-wrap">
        <HubHead eyebrow="Photo wall" title="On stage & on the road" />
        {editor && (
          <div className="mb-6 flex items-center gap-3 flex-wrap">
            <span className="text-xs text-white/60" data-testid="hub-photo-count">{visible.length} of {list.length} showing · tap the eye to show or hide</span>
            <AddPhotos slug={slug} onDone={changed} />
          </div>
        )}
      </div>
      {list.length ? (
        <div className="columns-1 sm:[column-count:var(--c2)] lg:[column-count:var(--c3)] 2xl:[column-count:var(--c4)] gap-0" style={{ "--c2": Math.min(n, 2), "--c3": Math.min(n, 3), "--c4": Math.min(n, 4) }} data-testid="hub-photo-grid" data-columns={Math.min(n, 4)}>
          {list.map((p) => <Tile key={p.photo_id} p={p} editor={editor} slug={slug} onOpen={() => setZoom(visible.indexOf(p))} onChanged={changed} />)}
        </div>
      ) : <p className="max-w-6xl mx-auto px-4 sm:px-6 pb-14 text-sm text-white/55">No photos yet — add some above. They show whole, never cropped.</p>}
      {zoom != null && zoom >= 0 && <PhotoLightbox photos={visible} index={zoom} onClose={() => setZoom(null)} />}
    </section>
  );
};
